import React from 'react';
import { Check } from 'lucide-react';
import { ONBOARDING_STEPS } from '../../../types/business';
import { cn } from '../../../lib/utils';

interface OnboardingProgressProps {
  currentStep: number;
  stepCompletion: Array<{ step: number; isComplete: boolean }>;
  onStepClick?: (step: number) => void;
}

export function OnboardingProgress({
  currentStep,
  stepCompletion,
  onStepClick,
}: OnboardingProgressProps) {
  return (
    <div className="w-full">
      {/* Desktop: horizontal stepper */}
      <div className="hidden md:flex items-center justify-between">
        {ONBOARDING_STEPS.map((step, idx) => {
          const completion = stepCompletion[idx];
          const isActive = currentStep === step.number;
          const isPast = completion?.isComplete && step.number < currentStep;
          const isClickable = step.number <= currentStep || completion?.isComplete;

          return (
            <React.Fragment key={step.key}>
              {/* Step circle + label */}
              <button
                type="button"
                onClick={() => isClickable && onStepClick?.(step.number)}
                disabled={!isClickable}
                className={cn(
                  'flex flex-col items-center gap-1.5 group transition-all',
                  isClickable ? 'cursor-pointer' : 'cursor-default'
                )}
              >
                <div
                  className={cn(
                    'w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium transition-all',
                    isActive && 'bg-primary text-white ring-4 ring-primary/20',
                    isPast && 'bg-primary text-white',
                    !isActive && !isPast && completion?.isComplete && 'bg-success text-white',
                    !isActive && !isPast && !completion?.isComplete && 'bg-gray-100 text-gray-400'
                  )}
                >
                  {isPast || (!isActive && completion?.isComplete) ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    step.number
                  )}
                </div>
                <span
                  className={cn(
                    'text-xs font-medium whitespace-nowrap transition-colors',
                    isActive ? 'text-primary' : 'text-body'
                  )}
                >
                  {step.label}
                </span>
              </button>

              {/* Connector line */}
              {idx < ONBOARDING_STEPS.length - 1 && (
                <div className="flex-1 h-0.5 mx-2 mt-[-18px]">
                  <div
                    className={cn(
                      'h-full rounded-full transition-colors',
                      stepCompletion[idx]?.isComplete
                        ? 'bg-primary'
                        : 'bg-gray-200'
                    )}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Mobile: compact progress bar */}
      <div className="md:hidden">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-heading">
            Step {currentStep} of {ONBOARDING_STEPS.length}
          </span>
          <span className="text-sm text-primary font-medium">
            {ONBOARDING_STEPS[currentStep - 1]?.label}
          </span>
        </div>
        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-primary rounded-full transition-all duration-500 ease-out"
            style={{ width: `${(currentStep / ONBOARDING_STEPS.length) * 100}%` }}
          />
        </div>
        {/* Step dots */}
        <div className="flex justify-between mt-2 px-1">
          {ONBOARDING_STEPS.map((step, idx) => {
            const completion = stepCompletion[idx];
            const isActive = currentStep === step.number;
            return (
              <button
                key={step.key}
                type="button"
                onClick={() => onStepClick?.(step.number)}
                className={cn(
                  'w-2.5 h-2.5 rounded-full transition-all',
                  isActive && 'bg-primary scale-125',
                  !isActive && completion?.isComplete && 'bg-primary/60',
                  !isActive && !completion?.isComplete && 'bg-gray-200'
                )}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
